import { useState } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { http, getErrorMessage } from "../../api/http";
import { StatusTag } from "../../components/common/StatusTag";
import { QrScanner } from "../../components/operation/QrScanner";
import { useToast } from "../../contexts/ToastContext";
import { formatDate, formatNumber } from "../../utils/format";
import { printPalletQrLabels } from "../stock/printPalletQr";

type ScanContentLot = {
    supplierLotNumber: string;
    expiresAt?: string | null;
    qualityStatus?: string;
    product: { id: number; sku: string; name: string; baseUom: string };
    supplier?: { id: number; name: string };
};
type ScanItem = { id: number; quantity: string; lot: ScanContentLot };
type ScanHandlingUnit = {
    id: number;
    code: string;
    scanCode?: string | null;
    status: string;
    location?: { id: number; code: string; name?: string | null } | null;
    items: ScanItem[];
};
type ScanLocation = { id: number; code: string; name?: string | null; purpose: string };
type ScanResult =
    | {
          kind: "LOCATION";
          location: ScanLocation;
          occupancy: {
              used: number;
              capacity: number;
              occupied: boolean;
              location: { handlingUnits: (ScanHandlingUnit & { items: ScanItem[] })[] };
          };
      }
    | { kind: "HANDLING_UNIT"; handlingUnit: ScanHandlingUnit };

function printPallet(hu: ScanHandlingUnit, onError: (message: string) => void) {
    const first = hu.items[0];
    printPalletQrLabels([
        {
            code: hu.code,
            scanCode: hu.scanCode,
            title: hu.code,
            subtitle: first?.lot.product.name,
            lines: [
                first ? `Lote ${first.lot.supplierLotNumber}` : "",
                hu.location?.code ? `Endereço ${hu.location.code}` : "",
            ].filter(Boolean),
        },
    ]).catch((cause) => onError(getErrorMessage(cause)));
}

/// Painel reutilizável do leitor: câmera + digitação (via QrScanner) e o
/// resultado (endereço ou palete). Usado no modal do cabeçalho.
export function ScannerContent() {
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<ScanResult>();
    const [notFound, setNotFound] = useState(false);

    async function lookup(raw: string) {
        const value = raw.trim();
        if (!value) return;
        try {
            setBusy(true);
            setNotFound(false);
            const { data } = await http.get<ScanResult>(`/scan/${encodeURIComponent(value)}`);
            setResult(data);
        } catch (cause) {
            setResult(undefined);
            setNotFound(true);
            toast.error("Código não encontrado", getErrorMessage(cause));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div>
            <QrScanner onRead={(value) => void lookup(value)} />

            {busy ? (
                <p className="validating-scan">
                    <i className="pi pi-spin pi-spinner" /> Buscando…
                </p>
            ) : null}

            {notFound ? (
                <div className="operation-callout" style={{ marginTop: "1rem" }}>
                    <i className="pi pi-exclamation-circle" />
                    <div>
                        <strong>Código não encontrado</strong>
                        <span>Confira o código do palete ou do endereço.</span>
                    </div>
                </div>
            ) : null}

            {result?.kind === "LOCATION" ? (
                <article className="entity-card" style={{ marginTop: "1rem" }}>
                    <div className="entity-card__top">
                        <div>
                            <span className="eyebrow">Endereço · {result.location.purpose}</span>
                            <h3>
                                {result.location.code}
                                {result.location.name ? ` · ${result.location.name}` : ""}
                            </h3>
                        </div>
                        <Tag
                            value={`${result.occupancy.used}/${result.occupancy.capacity}`}
                            severity={result.occupancy.occupied ? "warning" : "success"}
                            icon="pi pi-box"
                        />
                    </div>
                    <div className="stack-list">
                        {result.occupancy.location.handlingUnits.length ? (
                            result.occupancy.location.handlingUnits.map((hu) => (
                                <div className="line-card" key={hu.id}>
                                    <div>
                                        <strong>{hu.code}</strong>
                                        <small>
                                            {hu.items
                                                .map((it) => `${it.lot.product.name} · lote ${it.lot.supplierLotNumber}`)
                                                .join(" | ") || "Vazio"}
                                        </small>
                                    </div>
                                    <Button
                                        label="QR"
                                        icon="pi pi-print"
                                        text
                                        onClick={() => printPallet(hu, (m) => toast.error("Não foi possível imprimir", m))}
                                    />
                                </div>
                            ))
                        ) : (
                            <p className="muted">Endereço vazio.</p>
                        )}
                    </div>
                </article>
            ) : null}

            {result?.kind === "HANDLING_UNIT" ? (
                <article className="entity-card" style={{ marginTop: "1rem" }}>
                    <div className="entity-card__top">
                        <div>
                            <span className="eyebrow">
                                Palete{result.handlingUnit.location?.code ? ` · ${result.handlingUnit.location.code}` : ""}
                            </span>
                            <h3>{result.handlingUnit.code}</h3>
                        </div>
                        <Button
                            label="Imprimir QR"
                            icon="pi pi-print"
                            outlined
                            onClick={() =>
                                printPallet(result.handlingUnit, (m) => toast.error("Não foi possível imprimir", m))
                            }
                        />
                    </div>
                    <div className="stack-list">
                        {result.handlingUnit.items.length ? (
                            result.handlingUnit.items.map((item) => (
                                <div className="line-card" key={item.id}>
                                    <div>
                                        <strong>{item.lot.product.name}</strong>
                                        <small>
                                            Lote {item.lot.supplierLotNumber} · val. {formatDate(item.lot.expiresAt ?? "")}
                                        </small>
                                    </div>
                                    <span className="receipt-line__actions">
                                        <strong>
                                            {formatNumber(item.quantity)} {item.lot.product.baseUom}
                                        </strong>
                                        {item.lot.qualityStatus ? <StatusTag value={item.lot.qualityStatus} /> : null}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <p className="muted">Palete vazio.</p>
                        )}
                    </div>
                </article>
            ) : null}
        </div>
    );
}
